import React, { useLayoutEffect, useRef, useState } from 'react';
import { useAIChat } from '../../contexts/AIChat';
import { useToast } from '../../contexts/Toast';
import {
    ATTACHMENT_EXTENSIONS, ATTACHMENT_TYPES_LABEL, MAX_ATTACHMENTS, MAX_ATTACHMENT_SIZE_BYTES
} from './constants';

const extensionOf = name => (/\.[^.]+$/.exec(name.toLowerCase()) || [''])[0];

const formatResetDate = iso => {
    const date = iso ? new Date(iso) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
};

const AIChatComposer = ({ inputRef }) => {
    const {
        sendMessage, stopResponse, isResponding, usage
    } = useAIChat();
    const { errorToast } = useToast();

    const [text, setText] = useState('');
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);

    const canSend = !isResponding && (!!text.trim() || !!files.length);

    // Grow with the content up to the max-height set in the stylesheet.
    useLayoutEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
        // only scroll once the max-height cap is reached
        textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? 'auto' : 'hidden';
    }, [text, inputRef]);

    const submit = e => {
        e?.preventDefault();
        if (!canSend) return;
        sendMessage({ text, files });
        setText('');
        setFiles([]);
    };

    const onKeyDown = e => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) submit(e);
    };

    // Checked here as well as by Insight AI, which would otherwise refuse the whole question.
    const addFiles = e => {
        const picked = Array.from(e.target.files || [])
            .filter(file => !files.some(existing => existing.name === file.name));
        e.target.value = '';

        const unsupported = picked.filter(file => !ATTACHMENT_EXTENSIONS.includes(extensionOf(file.name)));
        const tooLarge = picked.filter(file => !unsupported.includes(file) && file.size > MAX_ATTACHMENT_SIZE_BYTES);
        const valid = picked.filter(file => !unsupported.includes(file) && !tooLarge.includes(file));
        const accepted = valid.slice(0, Math.max(0, MAX_ATTACHMENTS - files.length));

        if (unsupported.length) {
            errorToast(`${unsupported.map(file => file.name).join(', ')}: attach a ${ATTACHMENT_TYPES_LABEL} file.`);
        }
        if (tooLarge.length) {
            errorToast(`${tooLarge.map(file => file.name).join(', ')}: files can be up to 15 MB.`);
        }
        if (accepted.length < valid.length) {
            errorToast(`You can attach up to ${MAX_ATTACHMENTS} files to a message.`);
        }
        if (accepted.length) setFiles(prev => [...prev, ...accepted]);
    };

    const resetDate = formatResetDate(usage?.resetsAt);

    return (
        <div className="ai-chat_composer">
            <form className="ai-chat_input" onSubmit={submit}>
                {files.length ? (
                    <ul className="ai-chat_input_files" aria-label="Files to send">
                        {files.map(file => (
                            <li key={file.name} className="ai-chat_attachment">
                                <i className="zelpicon zelpicon-attachment" aria-hidden="true" />
                                <span>{file.name}</span>
                                <button
                                    type="button"
                                    className="ai-chat_attachment_remove"
                                    aria-label={`Remove ${file.name}`}
                                    onClick={() => setFiles(prev => prev.filter(f => f !== file))}>
                                    <i className="zelpicon zelpicon-close" aria-hidden="true" />
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : ''}

                <div className="ai-chat_input_row">
                    <button
                        type="button"
                        className="ai-chat_icon-button ai-chat_attach"
                        aria-label="Attach a file"
                        title="Attach a file"
                        onClick={() => fileInputRef.current?.click()}>
                        <i className="zelpicon zelpicon-attachment" aria-hidden="true" />
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ATTACHMENT_EXTENSIONS.join(',')}
                        multiple
                        hidden
                        onChange={addFiles} />

                    <textarea
                        ref={inputRef}
                        className="ai-chat_input_textarea"
                        rows={1}
                        value={text}
                        placeholder="Ask Insight AI about your farm..."
                        aria-label="Message Insight AI"
                        // opt out of writing-assistant extensions (Grammarly, LanguageTool)
                        // that overlay their own buttons on the input
                        data-gramm="false"
                        data-gramm_editor="false"
                        data-enable-grammarly="false"
                        data-lt-active="false"
                        onChange={e => setText(e.target.value)}
                        onKeyDown={onKeyDown} />

                    {isResponding ? (
                        <button
                            type="button"
                            className="ai-chat_send ai-chat_stop"
                            aria-label="Stop responding"
                            title="Stop"
                            onClick={stopResponse}>
                            <span className="ai-chat_stop_icon" aria-hidden="true" />
                        </button>
                    ) : (
                        <button
                            type="submit"
                            className="ai-chat_send"
                            aria-label="Send message"
                            title="Send"
                            disabled={!canSend}>
                            <i className="zelpicon zelpicon-back" aria-hidden="true" />
                        </button>
                    )}
                </div>
            </form>

            <div className="ai-chat_composer_settings">
                <p className="ai-chat_disclaimer">Insight AI can make mistakes. Check important information.</p>
                {usage ? (
                    <div className="ai-chat_usage" title={resetDate ? `Resets on ${resetDate}` : undefined}>
                        <span id="ai-chat-usage-label">{usage.label}</span>
                        <div
                            className="ai-chat_usage_bar"
                            role="progressbar"
                            aria-labelledby="ai-chat-usage-label"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={usage.percentage}>
                            <div className="ai-chat_usage_bar_fill" style={{ width: `${usage.percentage}%` }} />
                        </div>
                        <span className="ai-chat_usage_value">{`${usage.percentage}%`}</span>
                    </div>
                ) : ''}
            </div>

        </div>
    );
};

export default AIChatComposer;
